import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Icon, Input, Table, cn } from "@sagot/ui";
import { useState } from "react";
import { useBuildFormatters } from "../shared/format";
import type { EvalSet } from "../shared/types";
import { healthView } from "./eval-model";

interface SetsTableProps {
  sets: readonly EvalSet[];
  agentName: (agentId: string | null) => string;
  selectedId: string | null;
  runningIds: ReadonlySet<string>;
  canRun: boolean;
  onSelect: (set: EvalSet) => void;
  onOpen: (set: EvalSet) => void;
  onRun: (set: EvalSet) => void;
}

/** "Test sets" table: schedule, last run, score, health, inline Run (progress while queued/running). */
export function SetsTable({ sets, agentName, selectedId, runningIds, canRun, onSelect, onOpen, onRun }: SetsTableProps) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const [filter, setFilter] = useState("");
  const shown = sets.filter((s) => s.name.toLowerCase().includes(filter.trim().toLowerCase()));
  const cases = sets.reduce((n, s) => n + s.case_count, 0);
  return (
    <section className="card overflow-hidden" aria-labelledby="eval-sets-title">
      <div className="card-header flex-wrap gap-3">
        <div>
          <h2 id="eval-sets-title" className="card-title">{t("build.evaluations.sets.title")}</h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("build.evaluations.sets.summary", { sets: sets.length, cases })}</p>
        </div>
        <Input type="search" className="h-8 w-48 text-xs" aria-label={t("build.evaluations.sets.filterLabel")} placeholder={t("build.evaluations.sets.filter")} value={filter} onChange={(e) => { setFilter(e.target.value); }} />
      </div>
      {shown.length === 0 ? <EmptyState icon="filter" headingLevel={3} title={t("build.evaluations.sets.noMatch")} /> : (
        <Table>
          <thead>
            <tr>
              <th>{t("build.evaluations.sets.name")}</th>
              <th>{t("build.evaluations.sets.agent")}</th>
              <th>{t("build.evaluations.sets.schedule")}</th>
              <th>{t("build.evaluations.sets.lastRun")}</th>
              <th>{t("build.evaluations.sets.score")}</th>
              <th className="w-56">{t("build.evaluations.sets.status")}</th>
              <th className="text-right">{t("build.evaluations.sets.run")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((set) => {
              const health = healthView(set.health);
              const running = runningIds.has(set.id);
              return (
                <tr key={set.id} className={cn(set.id === selectedId && "bg-primary-soft")} data-testid="eval-set-row">
                  <td>
                    <div className="flex items-center gap-2.5">
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-ink"><Icon name="list-checks" className="size-4" /></span>
                      <div className="min-w-0">
                        <button type="button" className="text-left font-semibold hover:text-primary-ink hover:underline" onClick={() => { onOpen(set); }} aria-label={t("build.evaluations.sets.open", { name: set.name })}>{set.name}</button>
                        <div className="text-xs text-fg-subtle">{t("build.evaluations.sets.cases", { count: set.case_count })} · {t(`build.evaluations.sets.origins.${set.origin}`, { defaultValue: set.origin })}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-fg-muted">{agentName(set.agent_id)}</td>
                  <td><Badge><Icon name={set.schedule === "manual" ? "play" : set.schedule === "nightly" ? "clock" : "refresh-cw"} className="size-3" />{t(`build.evaluations.sets.schedules.${set.schedule}`, { defaultValue: set.schedule })}</Badge></td>
                  <td className="whitespace-nowrap text-fg-muted">
                    <button type="button" className="hover:underline" onClick={() => { onSelect(set); }}>{set.last_run_at ? fmt.dateTime(set.last_run_at) : t("build.evaluations.sets.never")}</button>
                  </td>
                  <td>
                    {set.last_score === null || set.last_score === undefined ? "—" : <><span className="font-bold tabular-nums">{set.last_score.toFixed(2)}</span> <span className="text-xs text-fg-subtle">{set.last_pass ?? 0}/{set.last_total ?? set.case_count}</span></>}
                  </td>
                  <td>
                    {running ? (
                      <span className="flex items-center gap-2 text-xs font-semibold text-fg-muted" role="status"><span className="typing-dot" />{t("build.evaluations.sets.progress")}</span>
                    ) : <Badge tone={health.tone}><Icon name={health.icon} className="size-3" />{t(`build.evaluations.sets.health.${set.health}`, { defaultValue: set.health })}</Badge>}
                  </td>
                  <td className="text-right">
                    <Button variant="secondary" size="sm" disabled={!canRun || running || set.case_count === 0} onClick={() => { onRun(set); }} aria-label={t("build.evaluations.sets.runLabel", { name: set.name })}>
                      <Icon name="play" />
                      {t("build.evaluations.sets.run")}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </section>
  );
}
